export interface ProviderCredential {
  id: string;
  organization_id: string;
  provider_name: string;
  api_key: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  config?: Record<string, any>;
}

export type ProviderName =
  | "OpenAI"
  | "Anthropic"
  | "DeepGram"
  | "ElevenLabs"
  | "GROQ"
  | "SmallestAI"
  | "LiveKit"
  | "Plivo"
  | "Twilio"
  | "CartesiaAI"
  | "GoogleGemini"
  | "Ultravox"
  | "SarvamAI_STT"
  | "SarvamAI_TTS"
  | "SarvamAI_LLM"
  | "OpenAIRealtime"
  | "CustomRealtime"
  | "GnaniAI"
  | "Mock";

export type ProviderCategory =
  | "LLM"
  | "TTS"
  | "STT"
  | "Realtime"
  | "Telephony"
  | "Testing";

export const PROVIDER_NAMES: ProviderName[] = [
  "OpenAI",
  "Anthropic",
  "DeepGram",
  "ElevenLabs",
  "GROQ",
  "SmallestAI",
  "LiveKit",
  "Plivo",
  "Twilio",
  "CartesiaAI",
  "GoogleGemini",
  "Ultravox",
  "SarvamAI_STT",
  "SarvamAI_TTS",
  "SarvamAI_LLM",
  "OpenAIRealtime",
  "CustomRealtime",
  "GnaniAI",
  "Mock",
];

export interface ProviderInfo {
  name: ProviderName;
  description: string;
  icon: string;
  fields: ProviderField[];
  category: ProviderCategory;
  docsUrl?: string;
}

export interface ProviderField {
  name: string;
  label: string;
  type: "text" | "password" | "select";
  placeholder?: string;
  required?: boolean;
  options?: { label: string; value: string }[];
  description?: string;
}

export const PROVIDER_INFO: Record<ProviderName, ProviderInfo> = {
  OpenAI: {
    name: "OpenAI",
    description: "API access for GPT models, DALL-E, and more",
    icon: "brain",
    category: "LLM",
    docsUrl: "https://platform.openai.com/docs/api-reference",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "sk-...",
        required: true,
        description: "Your OpenAI API key",
      },
      {
        name: "organization_id",
        label: "Organization ID",
        type: "text",
        placeholder: "org-...",
        required: false,
        description: "Optional: Your OpenAI organization ID",
      },
    ],
  },
  Anthropic: {
    name: "Anthropic",
    description: "Claude AI models for advanced reasoning and conversation",
    icon: "brain",
    category: "LLM",
    docsUrl: "https://docs.anthropic.com/claude/reference/getting-started",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "sk-ant-...",
        required: true,
        description: "Your Anthropic API key",
      },
    ],
  },
  DeepGram: {
    name: "DeepGram",
    description: "Speech-to-text transcription API",
    icon: "mic",
    category: "STT",
    docsUrl: "https://developers.deepgram.com/docs",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "dg-...",
        required: true,
        description: "Your DeepGram API key",
      },
    ],
  },
  ElevenLabs: {
    name: "ElevenLabs",
    description: "Text-to-speech voice generation",
    icon: "speaker",
    category: "TTS",
    docsUrl: "https://docs.elevenlabs.io/api-reference",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "...",
        required: true,
        description: "Your ElevenLabs API key",
      },
    ],
  },
  GROQ: {
    name: "GROQ",
    description: "AI model provider",
    icon: "bot",
    category: "LLM",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "...",
        required: true,
        description: "Your GROQ API key",
      },
    ],
  },
  SmallestAI: {
    name: "SmallestAI",
    description:
      "Lightning text-to-speech and Pulse speech-to-text. One key covers both.",
    icon: "speaker",
    category: "TTS",
    docsUrl: "https://docs.smallest.ai/",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "...",
        required: true,
        description: "Your SmallestAI API key (used for both Lightning TTS and Pulse STT)",
      },
      {
        name: "region",
        label: "Region",
        type: "select",
        options: [
          { label: "US East", value: "us-east" },
          { label: "US West", value: "us-west" },
          { label: "EU", value: "eu" },
          { label: "Asia Pacific", value: "ap" },
        ],
        required: false,
        description: "Optional: Select your SmallestAI region",
      },
    ],
  },
  LiveKit: {
    name: "LiveKit",
    description: "Real-time communication and SIP telephony provider",
    icon: "phone",
    category: "Telephony",
    docsUrl: "https://docs.livekit.io/",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "API...",
        required: true,
        description: "Your LiveKit API key",
      },
      {
        name: "api_secret",
        label: "API Secret",
        type: "password",
        placeholder: "...",
        required: true,
        description: "Your LiveKit API secret",
      },
      {
        name: "url",
        label: "Server URL",
        type: "text",
        placeholder: "wss://your-project.livekit.cloud",
        required: true,
        description: "Your LiveKit server URL",
      },
      {
        name: "sip_trunk_id",
        label: "SIP Trunk ID",
        type: "text",
        placeholder: "tr_...",
        required: false,
        description: "LiveKit SIP trunk ID for telephony",
      },
      {
        name: "caller_id",
        label: "Caller ID",
        type: "text",
        placeholder: "+1234567890",
        required: false,
        description: "Default caller ID for outbound calls",
      },
    ],
  },
  Plivo: {
    name: "Plivo",
    description: "Cloud telephony and SMS API platform",
    icon: "phone",
    category: "Telephony",
    docsUrl: "https://www.plivo.com/docs/",
    fields: [
      {
        name: "auth_id",
        label: "Auth ID",
        type: "text",
        placeholder: "MA...",
        required: true,
        description: "Your Plivo Auth ID",
      },
      {
        name: "auth_token",
        label: "Auth Token",
        type: "password",
        placeholder: "...",
        required: true,
        description: "Your Plivo Auth Token",
      },
      {
        name: "caller_id",
        label: "Default Caller ID",
        type: "text",
        placeholder: "+1234567890",
        required: false,
        description: "Default caller ID for outbound calls",
      },
      {
        name: "endpoint_url",
        label: "SIP Endpoint URL",
        type: "text",
        placeholder: "sip:phone.plivo.com",
        required: true,
        description: "SIP endpoint URL for your Plivo service",
      },
    ],
  },
  Twilio: {
    name: "Twilio",
    description: "Cloud communications platform for voice, SMS, and video",
    icon: "phone",
    category: "Telephony",
    docsUrl: "https://www.twilio.com/docs/voice/sip",
    fields: [
      {
        name: "account_sid",
        label: "Account SID",
        type: "text",
        placeholder: "AC...",
        required: true,
        description: "Your Twilio Account SID",
      },
      {
        name: "auth_token",
        label: "Auth Token",
        type: "password",
        placeholder: "...",
        required: true,
        description: "Your Twilio Auth Token",
      },
      {
        name: "phone_number",
        label: "Phone Number",
        type: "text",
        placeholder: "+1234567890",
        required: true,
        description: "Your Twilio phone number for outbound calls",
      },
      {
        name: "trunk_domain",
        label: "Trunk Domain",
        type: "text",
        placeholder: "yourtrunk.pstn.twilio.com",
        required: true,
        description: "Twilio SIP trunk domain (must end with .pstn.twilio.com)",
      },
    ],
  },
  Mock: {
    name: "Mock",
    description: "Mock telephony provider for testing and development",
    icon: "settings",
    category: "Testing",
    fields: [
      {
        name: "enabled",
        label: "Enable Mock Provider",
        type: "select",
        options: [
          { label: "Enabled", value: "true" },
          { label: "Disabled", value: "false" },
        ],
        required: true,
        description: "Enable or disable the mock telephony provider",
      },
      {
        name: "simulate_delay",
        label: "Simulate Delay (ms)",
        type: "text",
        placeholder: "1000",
        required: false,
        description: "Simulate network delay in milliseconds",
      },
    ],
  },
  CartesiaAI: {
    name: "CartesiaAI",
    description: "Ultra-low latency text-to-speech with emotion control",
    icon: "speaker",
    category: "TTS",
    docsUrl: "https://docs.cartesia.ai/",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "sk-...",
        required: true,
        description: "Your Cartesia AI API key",
      },
    ],
  },
  GoogleGemini: {
    name: "GoogleGemini",
    description: "Google's Gemini AI models for reasoning and conversation",
    icon: "brain",
    category: "LLM",
    docsUrl: "https://ai.google.dev/docs",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "AI...",
        required: true,
        description: "Your Google AI API key",
      },
    ],
  },
  Ultravox: {
    name: "Ultravox",
    description: "Multimodal real-time voice AI model with direct audio processing",
    icon: "brain",
    category: "LLM",
    docsUrl: "https://docs.ultravox.ai/",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "ultravox_...",
        required: true,
        description: "Your Ultravox API key for real-time voice processing",
      },
    ],
  },
  SarvamAI_STT: {
    name: "SarvamAI_STT",
    description: "Indic language speech-to-text models",
    icon: "mic",
    category: "STT",
    docsUrl: "https://docs.sarvam.ai/",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "sarvam_...",
        required: true,
        description: "Your Sarvam AI API key for speech recognition",
      },
    ],
  },
  SarvamAI_LLM: {
    name: "SarvamAI_LLM",
    description: "Indian-language chat models: Sarvam 105B, and Gemma 4 on Chat Completion V2 (beta)",
    icon: "brain",
    category: "LLM",
    docsUrl: "https://docs.sarvam.ai/api-reference/chat/chat-completions-v2",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "sarvam_...",
        required: true,
        description: "Your Sarvam AI API key. Gemma 4 needs a key with Chat Completion V2 beta access",
      },
    ],
  },
  GnaniAI: {
    name: "GnaniAI",
    description: "Vachana Indic speech-to-text, tuned for telephony audio",
    icon: "mic",
    category: "STT",
    docsUrl: "https://www.gnani.ai/",
    fields: [
      {
        name: "api_key",
        label: "API Key ID",
        type: "password",
        placeholder: "...",
        required: true,
        description: "Your Gnani Vachana API key ID (sent as the X-API-Key-ID header)",
      },
      {
        name: "organization_id",
        label: "Organization ID",
        type: "text",
        placeholder: "...",
        required: false,
        description: "Optional: Gnani organization ID, required by the batch REST endpoint",
      },
      {
        name: "user_id",
        label: "User ID",
        type: "text",
        placeholder: "...",
        required: false,
        description: "Optional: Gnani API user ID, required by the batch REST endpoint",
      },
    ],
  },
  SarvamAI_TTS: {
    name: "SarvamAI_TTS",
    description: "Indic language text-to-speech models",
    icon: "speaker",
    category: "TTS",
    docsUrl: "https://docs.sarvam.ai/",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "sarvam_...",
        required: true,
        description: "Your Sarvam AI API key for text-to-speech synthesis",
      },
    ],
  },
  OpenAIRealtime: {
    name: "OpenAIRealtime",
    description:
      "Realtime speech-to-speech model combining STT, reasoning, and TTS over a single connection",
    icon: "zap",
    category: "Realtime",
    docsUrl: "https://platform.openai.com/docs/guides/realtime",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "sk-...",
        required: true,
        description: "Your OpenAI API key with Realtime API access",
      },
      {
        name: "base_url",
        label: "Base URL",
        type: "text",
        placeholder: "https://api.openai.com/v1/realtime",
        required: false,
        description:
          "Optional: Override the Realtime API endpoint, e.g. for a proxy or Azure OpenAI deployment",
      },
    ],
  },
  CustomRealtime: {
    name: "CustomRealtime",
    description:
      "Bring your own OpenAI Realtime-compatible speech-to-speech endpoint",
    icon: "zap",
    category: "Realtime",
    fields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "...",
        required: true,
        description: "API key for your custom realtime endpoint",
      },
      {
        name: "base_url",
        label: "Base URL",
        type: "text",
        placeholder: "https://your-endpoint.example.com/v1/realtime",
        required: true,
        description: "Your custom OpenAI Realtime-compatible endpoint URL",
      },
    ],
  },
};

// Provider categories for organization
export const PROVIDER_CATEGORIES: Record<ProviderCategory, { name: string; icon: string; description: string }> = {
  LLM: {
    name: "Large Language Models",
    icon: "brain",
    description: "AI models for conversation and reasoning"
  },
  TTS: {
    name: "Text-to-Speech",
    icon: "speaker", 
    description: "Convert text to natural speech"
  },
  STT: {
    name: "Speech-to-Text",
    icon: "mic",
    description: "Convert speech to text transcription"
  },
  Realtime: {
    name: "Realtime Models",
    icon: "zap",
    description: "Speech-to-speech models handling STT, reasoning, and TTS in one connection"
  },
  Telephony: {
    name: "Telephony & Communication",
    icon: "phone",
    description: "Voice calling and telephony services"
  },
  Testing: {
    name: "Testing & Development",
    icon: "settings",
    description: "Mock providers for development"
  }
};

// Helper function to get providers by category
export const getProvidersByCategory = (category: ProviderCategory): ProviderName[] => {
  return PROVIDER_NAMES.filter(name => PROVIDER_INFO[name].category === category);
};
